import { useState } from "react";
import { Reorder } from "framer-motion";
import { Plus, Trash2, Lock } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Field, ImageUpload, PdfUpload } from "@/components/editor/fields";
import { DragRow } from "@/components/editor/LinksPanel";
import { BLOCK_LABELS, BUILTIN, newBlock } from "@/lib/cardUtils";

const mini = "rounded border bg-white px-2 py-1.5 text-sm w-full";

const ListEditor = ({ items, onChange, fields, addLabel, testId }) => (
  <div className="space-y-2">
    {items.map((it, i) => (
      <div key={i} className="flex gap-2">
        <div className={`grid flex-1 gap-2 ${fields.length === 3 ? "grid-cols-[1.2fr_1.4fr_0.7fr]" : "grid-cols-2"}`}>
          {fields.map(([k, ph]) => (
            <input key={k} className={mini} placeholder={ph} value={it[k] || ""} data-testid={`${testId}-${k}-${i}`}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />
          ))}
        </div>
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...items, Object.fromEntries(fields.map(([k]) => [k, ""]))])} data-testid={`${testId}-add`} className="flex items-center gap-1 text-xs text-terra"><Plus className="h-3.5 w-3.5" />{addLabel}</button>
  </div>
);

const BlockFields = ({ block, set, idx }) => {
  const d = block.data || {};
  const t = `block-${idx}`;
  switch (block.type) {
    case "text":
      return <><Field label="Titolo" value={d.title} onChange={(v) => set("title", v)} testId={`${t}-title`} /><Field label="Testo" area rows={5} value={d.body} onChange={(v) => set("body", v)} testId={`${t}-body`} /></>;
    case "image":
      return <><ImageUpload label="Immagine o banner" aspect="wide" value={d.url} onChange={(v) => set("url", v)} testId={`${t}-image`} /><Field label="Testo alternativo" value={d.alt} onChange={(v) => set("alt", v)} /><Field label="Didascalia" value={d.caption} onChange={(v) => set("caption", v)} /><Field label="Link al click (opzionale)" value={d.link} onChange={(v) => set("link", v)} /></>;
    case "hours":
      return <><Field label="Titolo" value={d.title} onChange={(v) => set("title", v)} /><ListEditor testId={`${t}-hours`} items={d.rows || []} onChange={(v) => set("rows", v)} fields={[["day", "Giorno"], ["hours", "Orario"]]} addLabel="Aggiungi riga" /><Field label="Nota" value={d.note} onChange={(v) => set("note", v)} /></>;
    case "divider":
      return (
        <div className="flex gap-2">{[["line", "Linea"], ["dots", "Puntini"], ["space", "Spazio"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => set("style", k)} className={`flex-1 rounded-md border px-2 py-1.5 text-xs ${d.style === k ? "border-zinc-900 bg-white" : "text-zinc-500"}`}>{l}</button>))}
        </div>
      );
    case "button":
      return (
        <>
          <Field label="Etichetta" value={d.label} onChange={(v) => set("label", v)} testId={`${t}-label`} />
          <Field label="URL" value={d.url} onChange={(v) => set("url", v)} testId={`${t}-url`} placeholder="https://" />
          <div className="flex gap-2">{[["filled", "Pieno"], ["outline", "Outline"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => set("variant", k)} className={`flex-1 rounded-md border px-2 py-1.5 text-xs ${d.variant === k ? "border-zinc-900 bg-white" : "text-zinc-500"}`}>{l}</button>))}
          </div>
        </>
      );
    case "whatsapp":
      return (
        <>
          <Field label="Titolo" value={d.title} onChange={(v) => set("title", v)} testId={`${t}-title`} />
          <Field label="Numero WhatsApp (vuoto = quello dei Contatti)" value={d.number} onChange={(v) => set("number", v)} testId={`${t}-number`} placeholder="+39 333 123 4567" />
          <Field label="Testo di esempio nel campo" value={d.placeholder} onChange={(v) => set("placeholder", v)} testId={`${t}-placeholder`} />
          <Field label="Testo del pulsante" value={d.button_label} onChange={(v) => set("button_label", v)} testId={`${t}-button-label`} />
          <p className="text-[11px] text-zinc-400">Il visitatore scrive il messaggio sulla card e WhatsApp si apre con il testo già pronto da inviare.</p>
        </>
      );
    case "file":
      return (
        <>
          <PdfUpload value={d.url} filename={d.filename} testId={`${t}-pdf`} onChange={(url, filename) => { set("url", url); set("filename", filename); }} />
          <Field label="Titolo sezione (opzionale)" value={d.title} onChange={(v) => set("title", v)} testId={`${t}-title`} />
          <Field label="Testo del pulsante" value={d.label} onChange={(v) => set("label", v)} testId={`${t}-label`} placeholder="Scarica il listino prezzi" />
          <Field label="Descrizione" value={d.description} onChange={(v) => set("description", v)} testId={`${t}-description`} placeholder="Aggiornato a gennaio" />
        </>
      );
    case "services":
      return <><Field label="Titolo" value={d.title} onChange={(v) => set("title", v)} /><ListEditor testId={`${t}-services`} items={d.items || []} onChange={(v) => set("items", v)} fields={[["name", "Servizio"], ["description", "Descrizione"], ["price", "Prezzo"]]} addLabel="Aggiungi servizio" /></>;
    default:
      return <Field label="Titolo sezione (opzionale)" value={d.title} onChange={(v) => set("title", v)} testId={`${t}-title`} />;
  }
};

const subtitleOf = (b) => b.data?.title || b.data?.label || (BUILTIN.includes(b.type) ? "Sezione di base" : "");

export const BlocksPanel = ({ card, update }) => {
  const blocks = card.blocks;
  const [added, setAdded] = useState(null);
  const addBlock = (type) => {
    const b = newBlock(type);
    setAdded(b.id);
    update("blocks", [...blocks, b]);
  };
  return (
    <div className="space-y-3" data-testid="editor-additional-section">
      <p className="text-xs text-zinc-500">Trascina per riordinare le sezioni della landing. L'intestazione e il pulsante "Salva contatto" restano sempre in alto.</p>
      <Reorder.Group axis="y" values={blocks} onReorder={(v) => update("blocks", v)} className="space-y-2">
        {blocks.map((b, idx) => (
          <DragRow key={b.id} value={b} defaultOpen={b.id === added} testId={`block-item-${b.type}-${idx}`} title={BLOCK_LABELS[b.type]} subtitle={subtitleOf(b)} visible={b.visible}
            icon={BUILTIN.includes(b.type) ? <Lock className="h-3.5 w-3.5 text-zinc-300" /> : null}
            onToggle={() => update(`blocks.${idx}.visible`, !b.visible)}
            onDelete={BUILTIN.includes(b.type) ? null : () => update("blocks", blocks.filter((x) => x.id !== b.id))}>
            <BlockFields block={b} idx={idx} set={(k, v) => update(`blocks.${idx}.data.${k}`, v)} />
          </DragRow>
        ))}
      </Reorder.Group>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid="add-block-button" className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed py-2.5 text-sm text-zinc-600 transition-colors hover:border-zinc-500 hover:text-zinc-900"><Plus className="h-4 w-4" />Aggiungi elemento</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          {["text", "image", "file", "whatsapp", "hours", "divider", "button", "services"].map((t) => (
            <DropdownMenuItem key={t} data-testid={`add-block-${t}`} onClick={() => addBlock(t)}>{BLOCK_LABELS[t]}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
